import React, { useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { APP_ROUTES } from "../../../PageRouting/approutes";
import { FaChevronRight, FaHeartbeat, FaBed } from "react-icons/fa";
import {
  MdOutlineLocalHospital,
  MdOutlineEmergency,
} from "react-icons/md";
import { GiScalpel } from "react-icons/gi";

/* ─── Accent / icon colours per module ─── */
const MODULE_THEME = {
  opd: {
    accent: "#189FAA",
    iconColor: "#189FAA",
    iconBg: "#E0F7FA",
    activeBg: "rgba(24, 159, 170, 0.04)",
  },
  emergency: {
    accent: "#EF4444",
    iconColor: "#EF4444",
    iconBg: "#FEE2E2",
    activeBg: "rgba(239, 68, 68, 0.04)",
  },
  icu: {
    accent: "#6366F1",
    iconColor: "#6366F1",
    iconBg: "#EDE9FE",
    activeBg: "rgba(99, 102, 241, 0.04)",
  },
  ot: {
    accent: "#8B5CF6",
    iconColor: "#8B5CF6",
    iconBg: "#F3E8FF",
    activeBg: "rgba(139, 92, 246, 0.04)",
  },
  wards: {
    accent: "#F59E0B",
    iconColor: "#F59E0B",
    iconBg: "#FEF3C7",
    activeBg: "rgba(245, 158, 11, 0.04)",
  },
};

/* ─── Card definitions ─── */
const CARDS = [
  {
    id: "opd",
    label: "OPD",
    route: APP_ROUTES.DASHBOARD,
    Icon: MdOutlineLocalHospital,
    mainLabel: "Today's Patients",
    stats: [
      { color: "#F59E0B", label: "Pending", valueKey: "opd_pending" },
      { color: "#22C55E", label: "Completed", valueKey: "opd_completed" },
    ],
  },
  {
    id: "emergency",
    label: "Emergency",
    route: APP_ROUTES.EMERGENCY,
    Icon: MdOutlineEmergency,
    mainLabel: "Active Cases",
    stats: [
      { color: "#EF4444", label: "Critical", valueKey: "emergency_critical" },
      { color: "#F59E0B", label: "Urgent", valueKey: "emergency_urgent" },
    ],
  },
  {
    id: "icu",
    label: "ICU",
    route: APP_ROUTES.ICUWARDS,
    Icon: FaHeartbeat,
    mainLabel: "Assigned Patients",
    stats: [
      { color: "#EF4444", label: "Critical", valueKey: "icu_critical" },
      { color: "#22C55E", label: "Stable", valueKey: "icu_stable" },
    ],
  },
  {
    id: "ot",
    label: "OT",
    route: APP_ROUTES.OPERATIONTHEATRE,
    Icon: GiScalpel,
    mainLabel: "Today's Surgeries",
    stats: [
      { color: "#F59E0B", label: "Scheduled", valueKey: "ot_scheduled" },
      { color: "#22C55E", label: "Completed", valueKey: "ot_completed" },
    ],
  },
  {
    id: "wards",
    label: "Wards",
    route: APP_ROUTES.INPATIENTS,
    Icon: FaBed,
    mainLabel: "Inpatients",
    stats: [
      { color: "#EF4444", label: "Critical", valueKey: "wards_critical" },
      { color: "#22C55E", label: "Stable", valueKey: "wards_stable" },
    ],
  },
];

/* ─── Mini stat badge ─── */
const StatBadge = ({ color, label, value }) => (
  <div className="flex items-center gap-1">
    <span
      className="w-2 h-2 rounded-full flex-shrink-0"
      style={{ backgroundColor: color }}
    />
    <span className="text-[10px] font-sans font-semibold text-gray-600 leading-none">
      {value}
    </span>
    <span className="text-[10px] font-sans text-gray-400 leading-none">
      {label}
    </span>
  </div>
);

/* ─── Default fallback counts (shown when API doesn't return a value) ─── */
const DEFAULT_COUNTS = {
  opd_pending: 12,
  opd_completed: 38,
  emergency_critical: 4,
  emergency_urgent: 9,
  icu_critical: 6,
  icu_stable: 14,
  ot_scheduled: 5,
  ot_completed: 3,
  wards_critical: 8,
  wards_stable: 27,
};

/* ─── Main component ─── */
function HimsModuleCards({ counts = {}, activeId, activeModule, className = "" }) {
  const history = useHistory();
  const location = useLocation();

  const currentPath = (location?.pathname || "").toLowerCase();

  const currentActiveId = useMemo(() => {
    const forced = activeId || activeModule;
    if (forced) return forced.toLowerCase();

    const path = currentPath;
    if (
      path === (APP_ROUTES.DASHBOARD || "").toLowerCase() ||
      path === (APP_ROUTES.NEWDASHBOARD || "").toLowerCase() ||
      path.endsWith("/dashboard") ||
      path.endsWith("/newdashboard")
    ) {
      return "opd";
    }
    if (path.includes("emergency")) {
      return "emergency";
    }
    if (path.includes("icu")) {
      return "icu";
    }
    if (path.includes("operation-theatre") || path.includes("/ot")) {
      return "ot";
    }
    if (
      path.includes("inpatient") ||
      path.includes("/wards") ||
      path.includes("ip-details")
    ) {
      return "wards";
    }
    return "";
  }, [activeId, activeModule, currentPath]);

  const getVal = (key) => {
    const v = counts?.[key];
    if (v !== undefined && v !== null) return v;
    return DEFAULT_COUNTS[key] ?? 0;
  };

  return (
    <div className={`w-full ${className}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {CARDS.map((card) => {
          const theme = MODULE_THEME[card.id] || MODULE_THEME.opd;
          const { accent, iconColor, iconBg, activeBg } = theme;
          const IconComp = card.Icon;
          const isActive = card.id === currentActiveId;

          return (
            <div
              key={card.id}
              onClick={() => {
                if (card.route && location.pathname !== card.route) {
                  history.push(card.route);
                }
              }}
              className={`group relative rounded-xl cursor-pointer transition-all duration-200 flex flex-col overflow-hidden ${
                isActive
                  ? "bg-white shadow-md -translate-y-0.5"
                  : "bg-white border border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5"
              }`}
              style={{
                paddingBottom: "14px",
                border: isActive ? `2px solid ${accent}` : "1px solid #E5E7EB",
                boxShadow: isActive
                  ? `0 10px 20px -3px ${accent}25, 0 4px 6px -4px ${accent}15`
                  : undefined,
                backgroundColor: isActive ? activeBg : "#FFFFFF",
              }}
            >
              {/* Top row: icon + title + status/chevron */}
              <div className="flex items-center justify-between px-3 pt-3">
                <div className="flex items-center gap-2.5 my-2">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-105"
                    style={{
                      backgroundColor: iconBg,
                      border: isActive ? `1.5px solid ${accent}` : "1px solid transparent",
                    }}
                  >
                    <IconComp style={{ color: iconColor, fontSize: "20px" }} />
                  </div>
                  <div>
                    <p
                      className={`text-sm font-sans leading-tight ${
                        isActive
                          ? "font-extrabold text-gray-900"
                          : "font-bold text-[#333333]"
                      }`}
                    >
                      {card.label}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {isActive && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white shadow-xs"
                      style={{ backgroundColor: accent }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                      Active
                    </span>
                  )}
                  <FaChevronRight
                    className={`text-xs transition-transform duration-200 group-hover:translate-x-0.5 ${
                      isActive ? "" : "text-gray-400"
                    }`}
                    style={{ color: isActive ? accent : undefined }}
                  />
                </div>
              </div>

              {/* Sub-label */}
              <p
                className={`px-3 mt-0.5 text-[10px] font-sans leading-none ${
                  isActive ? "text-gray-600 font-medium" : "text-gray-400"
                }`}
              >
                {card.mainLabel}
              </p>

              {/* Stat badges */}
              <div className="px-3 mt-2 flex gap-2 flex-wrap">
                {card.stats.map((s) => (
                  <StatBadge
                    key={s.valueKey}
                    color={s.color}
                    label={s.label}
                    value={getVal(s.valueKey)}
                  />
                ))}
              </div>

              {/* Bottom accent bar */}
              <div
                className="absolute bottom-0 left-0 w-full transition-all duration-200"
                style={{
                  height: isActive ? "4px" : "3px",
                  backgroundColor: accent,
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default HimsModuleCards;